import { useState } from 'react';

import { useT } from '@/i18n';
import shared from '@/pages/pages.module.css';
import { copyText } from '@/lib/clipboard';
import { shareLink } from '@/lib/links';

import { AppCard } from './AppCard';
import ui from './ui.module.css';
import { Button } from './Button';
import type { EmbeddedLinkProps } from './EmbeddedLink.types';

/**
 * What a bridge shows inside another site's frame: its link, to copy or to open in its own tab, and nothing that acts
 * on the bridge. The page of a bridge (share, delete, hand over a key) may not be framed, and the server says so with
 * `frame-ancestors`; the app moving on to it inside the same frame would slip past that, so in a frame it does not.
 */
export function EmbeddedLink({ bridgeId, bridgeKey }: EmbeddedLinkProps): React.JSX.Element {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const link = shareLink(bridgeId, bridgeKey);
  const copy = async (): Promise<void> => {
    setCopied(await copyText(link));
  };
  return (
    <AppCard className={shared.statusCard} testId="embedded-link" stepKey="embedded-link">
      <h2>{t('embeddedLink.title')}</h2>
      <p>{t('embeddedLink.text')}</p>
      <div className={shared.statusActions}>
        <Button onClick={() => void copy()} data-testid="embedded-copy">
          {copied ? t('common.copied') : t('embeddedLink.copy')}
        </Button>
        <a className={[ui.btn, ui.secondary].join(' ')} href={link} target="_blank" rel="noopener noreferrer" data-testid="embedded-open">
          {t('embeddedLink.open')}
        </a>
      </div>
    </AppCard>
  );
}
